import { Store } from '@ngrx/store';
import { IDeliveryOrder, IOrderDelivery, IOutletDelivery } from '@cakeiteasy/web-utils';
import { CartServiceV2 } from '../cart/services/cart-v2.service';
import { IDeliveryAddress } from '../users/models/i-delivery-address';
import { CART_DELIVERY_TYPES, DELIVERY_ORDER_DELIVERY_TYPES } from '../cart/constants/common';
import { UserApiService } from '../api-services/services/user-api.service';
import { DeliveryHelperService } from '../services/delivery-helper.service';
import { DeliveryApiService } from '../api-services/services/delivery-api.service';
import { BakeryOrdersDeliveryOrderModel } from '../delivery/models';
import { CartState, ICart } from '../cart';
import { ChooseDeliveryBase } from './choose-delivery-base';
import { isReturnStepRequired } from '../return-packaging/policy/return-packaging.policy';
import { UserState } from '../users/store/reducers/user.reducer';

export abstract class ChooseDeliveryExtendedBase extends ChooseDeliveryBase {
  public giftOutlets: IOutletDelivery[];
  public giftDelivery = new BakeryOrdersDeliveryOrderModel();

  /**Delivery does not always lead straight to payment: a cart with returnable packaging gets the
   * return step in between, and the confirm button has to say where it actually goes.*/
  public get nextBtnTextKey(): string {
    return isReturnStepRequired(this._cartService.cart)
      ? 'orderProcess.delivery.goToReturn'
      : 'orderProcess.delivery.goToPayment';
  }

  protected constructor(protected _cartService: CartServiceV2,
                        protected _cartStore: Store<CartState>,
                        protected _deliveryHelper: DeliveryHelperService,
                        protected _deliveryApi: DeliveryApiService,
                        protected _userApi: UserApiService,
                        protected _userStore?: Store<UserState>) {
    super(_cartService, _cartStore, _deliveryApi, _deliveryHelper, _userStore);
  }

  public giftDeliveryChanged(deliveryOrder: IOrderDelivery): Promise<IOrderDelivery> {
    const saving$ = this._cartService.saveGiftDelivery(deliveryOrder)
      .then(() => this._cartService.setDeliveryActiveStatus(true));

    return this._chainDeliverySaving(() => saving$);
  }

  public async initGiftOutlets() {
    const params = { excludePayInStoreOnly: true, pickupOnly: true };
    this.giftOutlets = await this._deliveryApi.getOutletsByBakeryId(this.bakery.id, params);
    return this.giftOutlets;
  }

  public initGiftDeliveryIfNecessary() {
    const { delivery } = this._cartService.cart;
    const type = delivery && delivery.deliveryType;
    if (this._deliveryHelper.isGiftDelivery(type)) {
      this.giftDelivery = { ...delivery, orderId: delivery.id } as IOrderDelivery;
    }
  }

  public setCartLines(cart: ICart) {
    this.cartLines = cart && cart.lines;
  }

  async mailDeliveryContinueProcess(delivery: IDeliveryOrder,
                                    options?: { dispatchCart: boolean, priceReducePercent?: number }): Promise<{ success?: boolean }> {
    try {
      const { MAIL: deliveryType } = DELIVERY_ORDER_DELIVERY_TYPES;
      const { DELIVERY: type } = CART_DELIVERY_TYPES;

      await this._cartService.saveDeliveryOrder({ ...delivery, type, deliveryType }, options);

      this._cartService.setDeliveryActiveStatus(true);
      return { success: true };
    } catch (e) {
      console.error(e);
      return { success: false };
    }
  }

  public saveUserDeliveryAddress(deliveryOrder: IDeliveryOrder) {
    const { deliveryCity, deliveryAddress, deliveryPostNumber, deliveryNote, deliveryPhone, deliveryUserName } = deliveryOrder;
    const savingAddress: IDeliveryAddress = {
      postNumber: deliveryPostNumber,
      cityName: deliveryCity,
      address: deliveryAddress,
      recipient: deliveryUserName,
      phone: deliveryPhone,
      note: deliveryNote,
      country: this.bakery.country.code
    };

    return this._userApi.addDeliveryAddress(savingAddress);
  }

  public getUserDeliveryAddresses(): Promise<IDeliveryAddress[] | null> {
    return this._userApi
      .getDeliveryAddresses()
      .then((addresses) => addresses
        .filter(a => a.country === this.bakery.country.code))
      .catch(() => {
        return null;
      });
  }
}
